/**
 * Entrypoint — finance-only stack.
 *
 * Изолированный entry для finance.<домен>. НЕ импортит main.tsx/router.tsx/
 * AppShell — не пересекается с dashboard/messenger/warehouse по коду.
 * Bundle содержит ТОЛЬКО:
 *   - FinanceShell (свой layout с финансовым сайдбаром)
 *   - Finance*-страницы
 *   - LoginPage / ResetPasswordPage / ProfilePage (общие)
 *   - AuthContext (общий — backend и кука те же)
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

import { AuthProvider } from "@/auth/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PeriodProvider } from "@/state/period";
import { buildFinanceRouter } from "@/router.finance";
import { bootstrapTheme } from "@/lib/useTheme";
import { setBrandFavicon } from "@/lib/setBrandFavicon";
import "@/styles/global.css";
// TG-style тема (светлый фон, зелёный акцент, Inter Tight, крупные радиусы).
// Используется в Production / Sales — теперь и в Finance для единого вида.
import "@/styles/production-tg.css";

bootstrapTheme();
setBrandFavicon("finance");
document.title = "Кирпичные беседки · Финансы";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = buildFinanceRouter(queryClient);

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("No #root element");

createRoot(rootEl).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <PeriodProvider>
            <RouterProvider router={router} />
          </PeriodProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
